import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Fade,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';
import { useBackgroundImage } from '../hooks/useBackgroundImage';
import { BackgroundSelector } from '../components/BackgroundSelector';
import { Logo } from '../components/Logo';
import { useTheme } from '@mui/material/styles';
import { CorporateTextField, CorporateButton } from '../components/ui';
import {
  createAuthPaperStyles,
  createBackgroundContainerStyles,
  createGradientTitleStyles,
  createPrimaryActionButtonStyles,
} from '../theme/utils';

export const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const theme = useTheme();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { currentBg, backgroundImages, selectedBackground, changeBackground, fallbackGradient, useFallback } = useBackgroundImage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showError(t('auth.passwordMismatch', 'Las contraseñas no coinciden'));
      return;
    }

    if (newPassword.length < 8) {
        showError(t('auth.passwordMinLength', 'La contraseña debe tener al menos 8 caracteres'));
        return;
    }

    setLoading(true);

    try {
      await apiService.resetPassword(newPassword);
      showSuccess(t('auth.passwordResetSuccess', 'Contraseña actualizada correctamente'));
      navigate('/ensayos', { replace: true });
    } catch (err: any) {
      showError(err.response?.data?.detail || t('auth.passwordResetError', 'Error al actualizar la contraseña'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={createBackgroundContainerStyles(
        theme,
        currentBg.url,
        useFallback,
        fallbackGradient
      )}
    >
      <BackgroundSelector
        selectedBackground={selectedBackground}
        backgroundImages={backgroundImages}
        onBackgroundChange={changeBackground}
        fallbackGradients={{
          '1': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '2': 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          '3': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        }}
      />

      <Fade in={true}>
        <Paper
          elevation={24}
          sx={{
            ...createAuthPaperStyles(theme),
            p: 5,
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Logo variant="auto" width={180} />
            </Box>
            <Typography
              component="h1"
              variant="h3"
              sx={{
                ...createGradientTitleStyles(theme),
                mb: 1,
              }}
            >
              {t('auth.resetPassword', 'Restablecer Contraseña')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('auth.resetPasswordSubtitle', 'Por seguridad, debes establecer una nueva contraseña para tu cuenta.')}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <CorporateTextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label={t('auth.newPassword', 'Nueva Contraseña')}
              type={showPassword ? 'text' : 'password'}
              id="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            
            <CorporateTextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t('auth.confirmPassword', 'Confirmar Contraseña')}
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <CorporateButton
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              colorVariant="accent"
              sx={{
                mt: 2,
                mb: 2,
                py: 1.5,
                ...createPrimaryActionButtonStyles(theme),
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.savePassword', 'Guardar Contraseña')}
            </CorporateButton>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};
